import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import GridViewIcon from "@mui/icons-material/GridView";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import EditAppKycDialog from "../../components/product-manager/EditAppKycDialog";
import BulkEntityUploadDialog from "../../components/product-manager/BulkEntityUploadDialog";
import AppKycAnalyticsPanel from "../../components/product-manager/AppKycAnalyticsPanel";
import { APP_KYC_BULK_CONFIG } from "../../utils/bulkUpload/bulkEntityConfigs";
import { useBookmarkableHubState } from "../../hooks/useBookmarkableHubState";
import { useAppKycs } from "../../hooks/useAppKycs";
import { apiService } from "../../api/services/apiService";
import type { AppKyc } from "../../types";
import { AppKeyStatus } from "../../types";
import { pmHubAddPath } from "../../utils/pmHubRoutes";

const APP_KEY_STATUS_OPTIONS = Object.values(AppKeyStatus);

type HubView = "records" | "analytics";

function filterAppKycByHubState(
  items: AppKyc[],
  state: ReturnType<typeof useBookmarkableHubState>,
): AppKyc[] {
  let result = items;

  if (state.debouncedQ) {
    const query = state.debouncedQ.toLowerCase();
    result = result.filter((item) => {
      const fields = [
        item.appKeyId,
        item.providerGatewayType,
        item.providerGatewayIdentifier,
        item.contactName ?? "",
        item.contactEmail ?? "",
        item.contactCompanyName ?? "",
      ];
      return fields.some((f) => (f ?? "").toLowerCase().includes(query));
    });
  }

  if (state.statuses.length) {
    result = result.filter((item) =>
      state.statuses.includes(item.appKeyStatus),
    );
  }

  return result;
}

const AppKycHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState({
    statusOptions: APP_KEY_STATUS_OPTIONS,
  });
  const { appKycs: fetched, loading, error, refresh } = useAppKycs();
  const [appKycs, setAppKycs] = useState<AppKyc[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [hubView, setHubView] = useState<HubView>("records");
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setAppKycs(fetched);
  }, [fetched]);

  const filtered = useMemo(
    () => filterAppKycByHubState(appKycs, hubState),
    [appKycs, hubState],
  );

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const editItem = useMemo(
    () => appKycs.find((a) => a.uuid === hubState.editUuid) ?? null,
    [appKycs, hubState.editUuid],
  );

  const handleToggleActive = async (item: AppKyc) => {
    setToggleLoadingId(item.uuid);
    const newStatus =
      item.appKeyStatus === AppKeyStatus.ACTIVE
        ? AppKeyStatus.INACTIVE
        : AppKeyStatus.ACTIVE;
    try {
      const updated = await apiService.updateAppKyc(item.uuid, {
        appKeyStatus: newStatus,
      });
      setAppKycs((prev) =>
        prev.map((a) => (a.uuid === updated.uuid ? updated : a)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  const handleCleanupComplete = () => {
    setAppKycs([]);
    refresh();
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="App KYC Hub"
        hubDescription="Manage app key onboarding records: gateway details, lifecycle dates, contacts, and provider metadata."
        addPath={pmHubAddPath("appKyc")}
        addLabel="Add App KYC"
        onBulkUpload={hubView === "records" ? () => setBulkOpen(true) : undefined}
        loading={loading}
        error={error}
        itemCount={hubView === "records" ? filtered.length : appKycs.length}
        hubState={hubState}
        statusOptions={APP_KEY_STATUS_OPTIONS}
        filterSidebarExtra={
          hubView === "analytics" ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Filters apply to the records view only.
            </Typography>
          ) : undefined
        }
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
          }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={hubView}
            onChange={(_, value: HubView | null) => {
              if (value) setHubView(value);
            }}
            aria-label="App KYC hub view"
          >
            <ToggleButton value="records" sx={{ textTransform: "none" }}>
              <GridViewIcon fontSize="small" sx={{ mr: 0.75 }} />
              Records
            </ToggleButton>
            <ToggleButton value="analytics" sx={{ textTransform: "none" }}>
              <AnalyticsIcon fontSize="small" sx={{ mr: 0.75 }} />
              Analytics
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {hubView === "analytics" ? (
          <AppKycAnalyticsPanel
            appKycs={appKycs}
            onCleanupComplete={handleCleanupComplete}
          />
        ) : paginated.length === 0 && !loading ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No App KYC records match your filters.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {paginated.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.uuid}>
                <EntityAssetCard
                  title={item.appKeyId}
                  subtitle={`${item.providerGatewayType} · ${item.providerGatewayIdentifier}`}
                  status={item.appKeyStatus}
                  enabled={item.appKeyStatus === AppKeyStatus.ACTIVE}
                  onToggleEnabled={() => handleToggleActive(item)}
                  toggleLoading={toggleLoadingId === item.uuid}
                  onEdit={() => hubState.setEditUuid(item.uuid)}
                  onDelete={async () => {
                    await apiService.deleteAppKyc(item.uuid);
                    setAppKycs((prev) => prev.filter((a) => a.uuid !== item.uuid));
                    refresh();
                  }}
                  metadata={
                    item.contactCompanyName ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.contactCompanyName}
                      </Typography>
                    ) : undefined
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}
      </EntityHubLayout>

      <BulkEntityUploadDialog
        open={bulkOpen}
        config={APP_KYC_BULK_CONFIG}
        onClose={() => setBulkOpen(false)}
        onComplete={() => refresh()}
      />

      <EditAppKycDialog
        appKyc={editItem}
        open={Boolean(hubState.editUuid && editItem)}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) =>
          setAppKycs((prev) =>
            prev.map((a) => (a.uuid === updated.uuid ? updated : a)),
          )
        }
      />
    </>
  );
};

export default AppKycHubPage;
